import React, {useState} from "react";
import {
  ColumnDef,
  PaginationState,
  Row,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./tableComponents/TableComponents";
import TuneIcon from "@mui/icons-material/Tune";
import DownloadIcon from "@mui/icons-material/Download";
import {SideWindow} from "../sideWindow/SideWindow";
import Pagination from "./pagination/Pagination";
import FilterWindow from "./filterWindow/FilterWindow";
import Placeholder from "../placeholder/Placeholder";

import {
  FilterableColumn,
  FilterRule,
  getFilteredData,
} from "./styledTableHelper";
import {exportToCSV} from "./exportHelper";

import styles from "./StyledTable.module.scss";

export type StyledTableProps<T> = {
  data: T[];
  columns: ColumnDef<T, any>[];
  classname?: string;
  onRowClick?: (row: T) => void;
  emptyState?: React.ReactNode;
  showPagination?: boolean;
  filterableColumns?: FilterableColumn[];
  getRowClassName?: (row: Row<T>) => string | undefined; // typed callback
};

export function StyledTable<T>({
  data,
  columns,
  classname,
  onRowClick,
  showPagination = true,
  filterableColumns,
  getRowClassName,
}: StyledTableProps<T>) {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const paginationRequired = data.length > 25;

  const filterable = filterableColumns && filterableColumns.length > 0;
  const [isFilterWindowOpen, setIsFilterWindowOpen] = useState(false);
  const [filterRules, setFilterRules] = useState<FilterRule[]>([]);
  const [draftFilterRules, setDraftFilterRules] =
    useState<FilterRule[]>(filterRules);

  const filteredData = React.useMemo(
    () => getFilteredData(data, filterRules, filterableColumns ?? []),
    [data, filterRules, filterableColumns]
  );

  const saveFilterRules = () => {
    setFilterRules(draftFilterRules);
    setPagination({pageIndex: 0, pageSize: 25});
    setIsFilterWindowOpen(false);
  };

  const exportData = () => {
    const rows = table.getCoreRowModel().rows;
    exportToCSV(rows);
  };

  const table = useReactTable({
    data: filterable ? filteredData : data,
    columns,
    state: {pagination},
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <Placeholder
        image="https://via.placeholder.com/150"
        title="No data found"
        text="No data found"
      />
    );
  }

  const NavigationBar = () => {
    return (
      <div className={styles.navigationBar}>
        {filterable && (
          <div
            className={styles.iconButtonContainer}
            onClick={() => setIsFilterWindowOpen(!isFilterWindowOpen)}
          >
            <p className={styles.iconButtonText}>
              Filters{" "}
              <span
                className={styles.filterCount}
              >{`(${filterRules?.length})`}</span>
            </p>
            <TuneIcon className={styles.iconButtonIcon} />
          </div>
        )}
        {showPagination && paginationRequired && <Pagination table={table} />}
        <div className={styles.exportContainer}>
          <p className={styles.totalOrdersText}>
            {filteredData.length} Results
          </p>
          <div className={styles.iconButtonContainer} onClick={exportData}>
            <p className={styles.iconButtonText}>Export </p>
            <DownloadIcon className={styles.iconButtonIcon} />
          </div>
        </div>
      </div>
    );
  };

  const StyledTableHeader = () => {
    return (
      <TableHeader>
        {table.getHeaderGroups().map((hg) => (
          <TableRow key={hg.id}>
            {hg.headers.map((header) => (
              <TableHead key={header.id}>
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
    );
  };

  const StyledTableRow = ({row}: {row: Row<T>}) => {
    const rowClass = getRowClassName?.(row);
    return (
      <TableRow
        key={row.id}
        className={rowClass}
        onClick={onRowClick ? () => onRowClick(row.original as T) : () => {}}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    );
  };

  return (
    <div className={[styles.tableRoot, classname].filter(Boolean).join(" ")}>
      <NavigationBar />
      <div className={styles.tableContainer}>
        <Table>
          <StyledTableHeader />
          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <StyledTableRow key={row.id} row={row} />
            ))}
          </TableBody>
        </Table>
      </div>
      {filterable && (
        <SideWindow
          title="Filter Orders"
          description="Add filters and save to apply them"
          children={
            <FilterWindow
              filterableColumns={filterableColumns}
              draftFilterRules={draftFilterRules}
              setDraftFilterRules={setDraftFilterRules}
            />
          }
          setIsWindowOpen={setIsFilterWindowOpen}
          isWindowOpen={isFilterWindowOpen}
          onSave={saveFilterRules}
          saveButtonText="Save"
        />
      )}
    </div>
  );
}
